import { apiGet, apiGetMeta, apiPost, apiPostWithHeaders } from './api-client.js';

/**
 * Typed wrappers over `GET/POST /files/{id}/movements`, `GET /files/{id}/valid-next-holders`
 * and `GET /me/tasks` (API_CONTRACT.md §13.2/§13.3). This is the ONLY file that knows
 * these paths.
 */

export interface MovementPartyRef {
  userId: string | null;
  userName: string | null;
  orgUnitId: string | null;
  orgUnitNameNe: string | null;
  orgUnitNameEn: string | null;
}

export interface MovementActor {
  userId: string | null;
  userName: string | null;
  roleCode: string | null;
}

export interface MovementAction {
  code: string;
  labelNe: string;
  labelEn: string;
}

export interface MovementRow {
  id: string;
  seqNo: number;
  action: MovementAction;
  from: MovementPartyRef;
  to: MovementPartyRef;
  actor: MovementActor;
  remarks: string | null;
  resultingStatusCode: string;
  reassignmentReasonCode: string | null;
  occurredAt: string;
  occurredDateBs: { year: number; month: number; day: number; formatted: string };
  correlationId: string;
}

export interface CursorPageMeta {
  nextCursor: string | null;
  hasMore: boolean;
}

export async function listMovements(
  fileId: string,
  params: { cursor?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<{ items: MovementRow[]; page: CursorPageMeta }> {
  const usp = new URLSearchParams();
  if (params.cursor !== undefined) usp.set('cursor', params.cursor);
  if (params.limit !== undefined) usp.set('limit', String(params.limit));
  const qs = usp.toString();
  const { data, meta } = await apiGetMeta<MovementRow[]>(`/files/${fileId}/movements${qs ? `?${qs}` : ''}`, signal);
  const page = (meta?.page as CursorPageMeta | undefined) ?? { nextCursor: null, hasMore: false };
  return { items: data, page };
}

/**
 * `GET /files/{id}/valid-next-holders` — the AUTHORITATIVE forward-target set (AC-10).
 * "Ask this endpoint rather than listing users" (`VERIFIED_RESPONSES.md`) — never build a
 * separate user picker.
 */
export interface NextHolderCandidate {
  userId: string;
  fullName: string;
  orgUnit: FileOrgUnitRefLike;
  designationLabelNe: string | null;
  designationLabelEn: string | null;
  /** `null` = unknown (availability tracking is not in this slice). Never assume `false`. */
  onLeave: boolean | null;
}

interface FileOrgUnitRefLike {
  id: string;
  nameNe: string;
  nameEn: string;
}

export function listValidNextHolders(fileId: string, signal?: AbortSignal): Promise<NextHolderCandidate[]> {
  return apiGet<NextHolderCandidate[]>(`/files/${fileId}/valid-next-holders`, signal);
}

/**
 * `POST /files/{id}/movements` — API_CONTRACT.md §13.3: `action ∈
 * forward|approve|reject|comment|complete` (the fifth, `complete`, exists on the server
 * but SCREEN_SPECS.md I9 names only the first four as this screen's buttons).
 * `expectedVersion` and `expectedHolderUserId` are the optimistic-concurrency pair — both
 * echoed from the file this action was opened against, never recomputed. `Idempotency-Key`
 * is REQUIRED here (§9.2): a double-submitted forward on a dropped connection must not
 * create two movements.
 */
export interface RecordMovementInput {
  action: 'forward' | 'approve' | 'reject' | 'comment' | 'complete';
  expectedVersion: number;
  expectedHolderUserId: string | null;
  toUserId?: string;
  remarks?: string;
}

/**
 * VERIFIED against a running server (not API_CONTRACT.md's prose, which does not
 * describe this body at all): `{movementId, seqNo, statusCode, holderUserId, version}`.
 * There is no `fileId` on this response and the status field is `statusCode`, not
 * `resultingStatusCode` (the name `GET .../movements` rows use for the same concept) —
 * this client only reads `version`/`statusCode` today, but the shape is recorded exactly
 * as observed so the next screen that needs `seqNo`/`holderUserId` does not have to
 * re-discover it. Reported in the handback.
 */
export interface RecordedMovement {
  movementId: string;
  seqNo: number;
  statusCode: string;
  holderUserId: string | null;
  version: number;
}

/**
 * R-035: the caller supplies the idempotency key — it must be minted ONCE, when the user's
 * intent begins (the action dialog opening, or the intake form loading), and reused across
 * every retry of that same intent. This function used to call `generateIdempotencyKey()`
 * inline, which meant a dropped-connection retry (or a double-click before React could
 * disable the button) minted a SECOND key and the server's replay gate — a real, tested
 * control shipped in `367c484` — never saw a repeat, so a double-submit still appended two
 * movements to a chain that cannot be corrected afterwards. Fixed by moving key generation
 * to the call site (`MovementActionModal`, once per modal-open) and threading it through
 * here as a required parameter, so this file can no longer regress into generating its own.
 */
export function recordMovement(
  fileId: string,
  input: RecordMovementInput,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<RecordedMovement> {
  return apiPostWithHeaders<RecordedMovement>(
    `/files/${fileId}/movements`,
    input,
    { 'Idempotency-Key': idempotencyKey },
    signal,
  );
}

/**
 * `POST /files/{id}/reassign` (API_CONTRACT.md §13.3) — a supervisor moving a file off a
 * holder who cannot act, NOT the holder's own forward. `expectedVersion` is the same
 * optimistic-concurrency token every write against this file needs.
 */
export interface ReassignFileInput {
  toUserId: string;
  reasonCode: string;
  remarks: string;
  expectedVersion: number;
}

export function reassignFile(fileId: string, input: ReassignFileInput, signal?: AbortSignal): Promise<RecordedMovement> {
  return apiPost<RecordedMovement>(`/files/${fileId}/reassign`, input, signal);
}

/** `POST /files/{id}/reopen` (§13.3) — only valid from `rejected`; `409 CONFLICT_STATE` otherwise. */
export interface ReopenFileInput {
  remarks: string;
  expectedVersion: number;
}

export function reopenFile(fileId: string, input: ReopenFileInput, signal?: AbortSignal): Promise<RecordedMovement> {
  return apiPost<RecordedMovement>(`/files/${fileId}/reopen`, input, signal);
}

/**
 * `POST /files/bulk-reassign` (§13.3) — a transferred officer's whole caseload in one call.
 * "Per-item results array; partial success is reported, never silently swallowed" — the
 * response is always `200`, and the per-item `ok`/`code`/`reason` is what the caller renders.
 */
export interface BulkReassignItem {
  fileId: string;
  expectedVersion: number;
}

export interface BulkReassignInput {
  items: BulkReassignItem[];
  toUserId: string;
  reasonCode: string;
  remarks: string;
}

export interface BulkReassignItemResult {
  fileId: string;
  ok: boolean;
  code?: string;
  reason?: string;
  movementId?: string;
}

export interface BulkReassignResult {
  results: BulkReassignItemResult[];
  succeeded: number;
  failed: number;
}

export function bulkReassignFiles(input: BulkReassignInput, signal?: AbortSignal): Promise<BulkReassignResult> {
  return apiPost<BulkReassignResult>('/files/bulk-reassign', input, signal);
}

/** `GET /me/tasks` — deliberately narrower than `GET /files` (`VERIFIED_RESPONSES.md`): a work queue, not a register row. */
export interface MyTaskRow {
  id: string;
  trackingCode: string;
  subject: string;
  statusCode: string;
  dueAt: string | null;
  isOverdue: boolean;
}

export function listMyTasks(params: { isOverdue?: boolean } = {}, signal?: AbortSignal): Promise<MyTaskRow[]> {
  const qs = params.isOverdue === undefined ? '' : `?isOverdue=${params.isOverdue}`;
  return apiGet<MyTaskRow[]>(`/me/tasks${qs}`, signal);
}

export { ApiError, NetworkError } from './api-client.js';
